/**
 * Finnish business ID (Y-tunnus) helpers.
 *
 * A business ID is seven digits, a hyphen and a check digit calculated modulo 11
 * from the weights below. Validating it in the UI keeps typos from reaching YTJ.
 */

const CHECK_DIGIT_WEIGHTS = [7, 9, 10, 5, 8, 4, 2]

/**
 * Calculate the check digit of the seven digit body of a business ID.
 * Returns null when the digits leave no valid check digit.
 */
function checkDigit(digits: string): string | null {
  const sum = CHECK_DIGIT_WEIGHTS.reduce((acc, weight, idx) => acc + weight * parseInt(digits[idx], 10), 0)
  const remainder = sum % 11
  if (remainder === 0) {
    return '0'
  }
  // A remainder of one leaves no valid check digit, so such business IDs are not issued.
  if (remainder === 1) {
    return null
  }
  return `${11 - remainder}`
}

/**
 * Check that the value is a business ID as `1234567-8` and that the check digit matches.
 */
export function isValidBusinessId(value: string | null | undefined): boolean {
  if (!value) {
    return false
  }
  const match = /^(\d{7})-(\d)$/.exec(value.trim())
  if (!match) {
    return false
  }
  return checkDigit(match[1]) === match[2]
}

/**
 * Clean up user input into the `1234567-8` form as far as possible.
 */
export function formatBusinessId(value: string): string {
  const digits = value.replace(/[^\d]/g, '').substring(0, 8)
  if (digits.length <= 7) {
    return digits
  }
  return `${digits.substring(0, 7)}-${digits.substring(7)}`
}
